<script setup>
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()

defineProps({
  pending:       { type: Array, default: () => [] },
  approved:      { type: Array, default: () => [] },
  pending_count: { type: Number, default: 0 },
})

function approve(r) {
  router.patch(`/admin/reviews/${r.id}/approve`, {}, { preserveScroll: true })
}

function reject(r) {
  router.patch(`/admin/reviews/${r.id}/reject`, {}, { preserveScroll: true })
}
</script>

<template>
  <PageShell :title="t('admin.reviews.title')">
    <section class="space-y-3">
      <h2 class="section-title">
        {{ t('admin.reviews.pending_title') }}
        <span v-if="pending_count > 0" class="pending-badge">{{ pending_count }}</span>
      </h2>

      <p v-if="pending.length === 0" class="empty">{{ t('admin.reviews.empty') }}</p>

      <div v-for="r in pending" :key="r.id" class="card">
        <div class="card__main">
          <div class="info">
            <div class="info__title">{{ r.author_name }}</div>
            <div class="info__tour">{{ r.tour?.title }}</div>
            <div class="info__stars">
              <span v-for="i in 5" :key="i" class="star" :class="{ 'star--filled': i <= r.rating }">★</span>
            </div>
            <div class="info__body">{{ r.body }}</div>
            <div v-if="r.email" class="info__email">{{ r.email }}</div>
          </div>

          <div class="actions">
            <UiButton size="sm" variant="primary" @click="approve(r)">{{ t('admin.reviews.approve') }}</UiButton>
            <UiButton size="sm" variant="ghost" @click="reject(r)">{{ t('admin.reviews.reject') }}</UiButton>
          </div>
        </div>
      </div>
    </section>

    <section v-if="approved.length" class="space-y-3 mt-8">
      <h2 class="section-title">{{ t('admin.reviews.approved_title') }}</h2>
      <div v-for="r in approved" :key="r.id" class="card">
        <div class="card__main">
          <div class="info">
            <div class="info__title">{{ r.author_name }}</div>
            <div class="info__tour">{{ r.tour?.title }}</div>
            <div class="info__stars">
              <span v-for="i in 5" :key="i" class="star" :class="{ 'star--filled': i <= r.rating }">★</span>
            </div>
            <div class="info__body">{{ r.body }}</div>
          </div>
        </div>
      </div>
    </section>
  </PageShell>
</template>

<style scoped>
.section-title {
  font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--muted); margin: 0;
}
.empty {
  padding: 1.5rem; text-align: center; color: var(--muted);
  border: 1px dashed var(--line); border-radius: var(--radius-md, 8px);
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md, 8px);
  padding: 0.85rem 1rem;
}
.card__main { display: flex; gap: 1rem; align-items: flex-start; }
.info { flex: 1; min-width: 0; }
.info__title { font-weight: 600; color: var(--ink); }
.info__tour { font-size: 0.8125rem; color: var(--inkSoft); }
.info__stars { margin: 0.25rem 0; }
.star { color: var(--muted); font-size: 1rem; }
.star--filled { color: var(--accent); }
.info__body { font-size: 0.875rem; color: var(--ink); margin-top: 0.25rem; }
.info__email { font-size: 0.8125rem; color: var(--inkSoft); margin-top: 0.2rem; }
.actions { display: flex; gap: 0.4rem; flex-shrink: 0; }
.pending-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 1.35rem; height: 1.35rem; padding: 0 0.35rem;
  border-radius: 999px; font-size: 0.75rem; font-weight: 700; line-height: 1;
  background: var(--danger); color: #fff; vertical-align: middle; margin-left: 0.4rem;
}
</style>
